import type { AxiosError } from 'axios';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import initApi from '@/api/initApiV2';

import type {
  OrganizationConfig,
  OrganizationQuota,
  OrganizationsApiGetOrganizationConfigRequest,
  OrganizationsApiGetOrganizationQuotaRequest,
  OrganizationsApiUpdateOrganizationConfigRequest,
  OrganizationsApiUpdateOrganizationConfigRiskRequest,
  OrganizationsApiUpdateOrganizationInternalStatusRequest,
  OrganizationsApiUpdateOrganizationRequest,
} from '@/clients/v2';
import { OrganizationsApi } from '@/clients/v2';

import { handleApiError } from '@/api/handleApiError.ts';
import { useAuthStore } from '@/stores/AuthStore.ts';

export const useOrganizationsStore = defineStore('organizations-store-v2', () => {
  const configData = ref<OrganizationConfig | null>(null);
  const organizationQuota = ref<OrganizationQuota | null>(null);
  const vendorsEnabled = ref<boolean>(false);
  const usersEnabled = ref<boolean>(false);
  const evidenceScoringEnabled = ref<boolean>(false);
  const controlAssessmentEnabled = ref<boolean>(false);
  const chatEnabled = ref<boolean>(false);
  const riskManagementEnabled = ref<boolean>(false);
  const governanceEnabled = ref<boolean>(false);
  const agentTestsEnabled = ref<boolean>(false);
  const currentOrganization = computed(() => {
    return useAuthStore().user?.organization;
  });

  const getOrganizationConfig = async (
    params: OrganizationsApiGetOrganizationConfigRequest,
    updateCurrent: boolean = false,
  ) => {
    try {
      const organizationApi = await initApi(OrganizationsApi);

      const response = await organizationApi?.getOrganizationConfig(params);

      if (!response) return;

      if (updateCurrent) {
        configData.value = response.data;

        vendorsEnabled.value =
          response.data.flags?.vendor_management_enabled_for_entire_organization ?? true;
        usersEnabled.value =
          response.data.flags?.user_management_enabled_for_entire_organization ?? true;
        evidenceScoringEnabled.value =
          response.data.flags?.evidence_scoring_enabled_for_entire_organization ?? false;
        controlAssessmentEnabled.value =
          response.data.flags?.control_assessment_enabled_for_entire_organization ?? false;
        chatEnabled.value = response.data.flags?.scout_enabled_for_entire_organization ?? false;
        riskManagementEnabled.value =
          response.data.flags?.risk_management_enabled_for_entire_organization ?? false;
        governanceEnabled.value =
          response.data.flags?.governance_and_compliance_for_entire_organization ?? false;
        agentTestsEnabled.value =
          response.data.flags?.agent_test_enabled_for_entire_organization ?? false;
      }

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updateOrganizationConfig = async (
    params: OrganizationsApiUpdateOrganizationConfigRequest,
    updateCurrent = true,
  ) => {
    try {
      const organizationApi = await initApi(OrganizationsApi);

      const response = await organizationApi?.updateOrganizationConfig(params);

      if (!response) return;

      if (updateCurrent) {
        configData.value = response.data;

        vendorsEnabled.value =
          response.data.flags?.vendor_management_enabled_for_entire_organization ?? true;
        usersEnabled.value =
          response.data.flags?.user_management_enabled_for_entire_organization ?? true;
        evidenceScoringEnabled.value =
          response.data.flags?.evidence_scoring_enabled_for_entire_organization ?? false;
        controlAssessmentEnabled.value =
          response.data.flags?.control_assessment_enabled_for_entire_organization ?? false;
        chatEnabled.value = response.data.flags?.scout_enabled_for_entire_organization ?? false;
        riskManagementEnabled.value =
          response.data.flags?.risk_management_enabled_for_entire_organization ?? false;
        governanceEnabled.value =
          response.data.flags?.governance_and_compliance_for_entire_organization ?? false;
        agentTestsEnabled.value =
          response.data.flags?.agent_test_enabled_for_entire_organization ?? false;
      }

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updateOrganizationConfigRisk = async (
    params: OrganizationsApiUpdateOrganizationConfigRiskRequest,
  ) => {
    try {
      const organizationApi = await initApi(OrganizationsApi);
      const response = await organizationApi?.updateOrganizationConfigRisk(params);

      if (!response) return;

      configData.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getOrganizationSsoStatus = async (organizationId: string) => {
    try {
      const organizationApi = await initApi(OrganizationsApi);

      const response = await organizationApi?.getOrganizationSsoStatus({ organizationId });

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);

      throw error;
    }
  };

  const getOrganizationQuota = async (params: OrganizationsApiGetOrganizationQuotaRequest) => {
    try {
      const organizationApi = await initApi(OrganizationsApi);

      const response = await organizationApi?.getOrganizationQuota(params);

      if (!response) return;

      organizationQuota.value = response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updateOrganization = async (params: OrganizationsApiUpdateOrganizationRequest) => {
    try {
      const organizationApi = await initApi(OrganizationsApi);

      const response = await organizationApi?.updateOrganization(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updateOrganizationInternalStatus = async (
    params: OrganizationsApiUpdateOrganizationInternalStatusRequest,
  ) => {
    try {
      const organizationApi = await initApi(OrganizationsApi);

      const response = await organizationApi?.updateOrganizationInternalStatus(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const resetStore = () => {
    configData.value = null;
    organizationQuota.value = null;
    vendorsEnabled.value = false;
    usersEnabled.value = false;
    evidenceScoringEnabled.value = false;
    controlAssessmentEnabled.value = false;
    chatEnabled.value = false;
    riskManagementEnabled.value = false;
    governanceEnabled.value = false;
    agentTestsEnabled.value = false;
  };

  return {
    configData,
    evidenceScoringEnabled,
    usersEnabled,
    vendorsEnabled,
    controlAssessmentEnabled,
    chatEnabled,
    riskManagementEnabled,
    governanceEnabled,
    agentTestsEnabled,
    updateOrganizationConfig,
    updateOrganizationConfigRisk,
    updateOrganizationInternalStatus,
    currentOrganization,
    updateOrganization,
    getOrganizationConfig,
    getOrganizationSsoStatus,
    getOrganizationQuota,
    organizationQuota,
    resetStore,
  };
});
